import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Check, Sparkles, X } from 'lucide-react';
import type { ChatSuggestedEvent, ChatSuggestedService } from '@/lib/api/chatCreation.service';

interface ServiceReviewCardProps {
  services: ChatSuggestedService[];
  selectedServiceDefinitionIds?: number[];
  selectedEvents?: ChatSuggestedEvent[];
  onConfirm?: (selectedServiceDefinitionIds: number[]) => void;
  onSkip?: () => void;
}

const PRICE_RANGE_LABELS: Record<'low' | 'mid' | 'high', string> = {
  low: 'Low cost',
  mid: 'Medium cost',
  high: 'High cost',
};

export const ServiceReviewCard: React.FC<ServiceReviewCardProps> = ({
  services,
  selectedServiceDefinitionIds,
  selectedEvents,
  onConfirm,
  onSkip,
}) => {
  const defaultSelection = useMemo(() => {
    if (Array.isArray(selectedServiceDefinitionIds)) {
      return new Set(selectedServiceDefinitionIds);
    }

    return new Set(
      services
        .filter((service) => service.selectedByDefault !== false)
        .map((service) => service.serviceDefinitionId),
    );
  }, [services, selectedServiceDefinitionIds]);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(defaultSelection);

  useEffect(() => {
    setSelectedIds(defaultSelection);
  }, [defaultSelection]);

  const groupedServices = useMemo(() => {
    const grouped = new Map<string, ChatSuggestedService[]>();
    for (const service of services) {
      const category = service.category?.trim() || 'Other';
      if (!grouped.has(category)) {
        grouped.set(category, []);
      }
      grouped.get(category)?.push(service);
    }

    return Array.from(grouped.entries());
  }, [services]);

  if (services.length === 0) {
    return null;
  }

  const linkedEventLabels = (selectedEvents ?? []).map((event) => event.title).filter(Boolean);

  const toggleService = (serviceDefinitionId: number) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(serviceDefinitionId)) {
        next.delete(serviceDefinitionId);
      } else {
        next.add(serviceDefinitionId);
      }
      return next;
    });
  };

  return (
    <Card className="border-primary/20 bg-primary/5 max-w-xl w-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          Suggested Services
        </CardTitle>
        <p className="text-xs text-muted-foreground">Choose services to add to your shortlist</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {groupedServices.map(([category, categoryServices]) => (
          <div key={category} className="space-y-2 rounded-md border bg-background p-2">
            <p className="text-sm font-medium">{category}</p>
            <div className="flex flex-wrap gap-1.5">
              {linkedEventLabels.length > 0 ? linkedEventLabels.map((eventTitle) => (
                <Badge key={`${category}-${eventTitle}`} variant="secondary" className="text-[11px]">
                  Applies to {eventTitle}
                </Badge>
              )) : (
                <Badge variant="outline" className="text-[11px]">
                  Applies across the celebration
                </Badge>
              )}
            </div>
            {categoryServices.map((service) => (
              <div key={service.serviceDefinitionId} className="flex items-start gap-2">
                <input
                  type="checkbox"
                  aria-label={`Select service ${service.name}`}
                  checked={selectedIds.has(service.serviceDefinitionId)}
                  onChange={() => toggleService(service.serviceDefinitionId)}
                  className="mt-0.5 h-4 w-4 rounded border border-input"
                />
                <div>
                  <p className="text-sm">{service.name}</p>
                  {service.typicalPriceRange && (
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      {PRICE_RANGE_LABELS[service.typicalPriceRange]}
                    </p>
                  )}
                  {linkedEventLabels.length > 0 && (
                    <p className="text-[11px] text-muted-foreground">
                      Linked events: {linkedEventLabels.join(', ')}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </CardContent>
      <CardFooter className="flex gap-2 pt-2">
        {onConfirm && (
          <Button
            size="sm"
            className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700"
            onClick={() => onConfirm(Array.from(selectedIds))}
          >
            <Check className="h-4 w-4 mr-1" />
            Confirm Services
          </Button>
        )}
        {onSkip && (
          <Button size="sm" variant="outline" className="flex-1" onClick={onSkip}>
            <X className="h-4 w-4 mr-1" />
            Skip Services
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};
